import { ReactNode, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Cpu, Infinity as InfinityIcon } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import ParticleBackground from './ParticleBackground';
import CookieConsent from './CookieConsent';
import { useVisitorTracking, trackPageView } from '../hooks/useSiteAnalytics';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isHidden, setIsHidden] = useState(false);

  // Send an anonymous page-view ping on every route change (respects cookie consent)
  useVisitorTracking();

  // Re-fire on route changes (Router v6 doesn't always trigger popstate)
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleModalOpen = () => setIsHidden(true);
    const handleModalClose = () => setIsHidden(false);
    
    window.addEventListener('modalOpen', handleModalOpen);
    window.addEventListener('modalClose', handleModalClose);
    
    return () => {
      window.removeEventListener('modalOpen', handleModalOpen);
      window.removeEventListener('modalClose', handleModalClose);
    };
  }, []);
  
  const sections = [
    {
      key: 'unlimited',
      name: 'Cybertronic Unlimited',
      href: '/unlimited',
      color: 'text-green-500',
      borderColor: 'border-green-500/30',
      icon: <InfinityIcon className="w-4 h-4" />
    },
    {
      key: 'robotics', 
      name: 'Cybertronic Robotics',
      href: '/robotics',
      color: 'text-blue-500',
      borderColor: 'border-blue-500/30',
      icon: <Cpu className="w-4 h-4" />
    },
    {
      key: 'sex-machines',
      name: 'Ostatní',
      href: '/sex-machines', 
      color: 'text-pink-500',
      borderColor: 'border-pink-500/30',
      icon: <Heart className="w-4 h-4" />
    }
  ];
  
  const getCurrentSection = () => {
    if (location.pathname.includes('/unlimited')) return 'unlimited';
    if (location.pathname.includes('/robotics')) return 'robotics';
    if (location.pathname.includes('/sex-machines')) return 'sex-machines';
    return null;
  };
  
  const currentSection = getCurrentSection();
  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' || 
                  localStorage.getItem('admin_logged_in') === 'true';
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      <ParticleBackground />
      
      <Navbar />
      
      <main className="relative z-10">
        {children}
      </main>
      
      <Footer />

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Neon accent overlays */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#EE1C23] to-transparent opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#EE1C23] to-transparent opacity-30"></div>
        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-[#EE1C23] to-transparent opacity-0"></div>
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-transparent via-[#EE1C23] to-transparent opacity-0"></div>
      </div>
    </div>
  );
}